import AddLink from "../../components/form/addLink.comp";
import Layout from "../../components/layouts/layout.comp";
import { useTranslation } from "../../core/translation.core";
import { useQuery } from "@tanstack/react-query";
import { list } from "../../core/productFile.core";
import type { ProductFile, ProductFileFilter, ProductFileListResponse } from "../../types/productFile";
import Link from "next/link";
import { format } from "date-fns";
import { useForm, UseFormReturn, useWatch } from "react-hook-form";
import { ListSection } from "../../components/reuseables/list.comp";
import ErrorCenterIcon from "../../icons/exclamationTriangleCenter.icon";
import LoadingOvalCenterIcon from "../../icons/loadingOvalCenter.icon";
import PencilIcon from "../../icons/pencil.icon";
import ArrowDownTrayIcon from "../../icons/arrowDownTray.icon";
import DeleteProductFileDialog from "../../components/product/deleteProductFileDialog.comp";
import { useRef } from "react";
import TrashIcon from "../../icons/trash.icon";

export default function ProductFile() {
  const { t, msg } = useTranslation();
  return (
    <Layout title={t(msg.product.productFile)} titleBtn={<AddLink key={1} id="productfile-create" href="/productFile/create" />}>
      <Content />
    </Layout>
  );
}

function Content() {
  const { t, msg } = useTranslation();
  const methods = useForm<ProductFileFilter>({
    defaultValues: {
      page: 1,
    },
  });

  return (
    <div className="w-full">
      {/* <ProductSearchFilter methods={methods} /> */}
      <List methods={methods} />
    </div>
  );
}

function ProductSearchFilter({ methods }: { methods: UseFormReturn<ProductFileFilter, any> }) {
  return <div>Search</div>;
}

function List({ methods }: { methods: UseFormReturn<ProductFileFilter, any> }) {
  const { t, msg } = useTranslation();
  const { page, type, productId } = useWatch(methods);
  const { data, isLoading, isError, error } = useQuery<ProductFileListResponse>({
    queryKey: ["productFiles", type, page, productId],
    queryFn: () => list({ page: page!, type: type, productId: productId }),
  });

  if (isError) return <ErrorCenterIcon reason={error.message} />;
  if (isLoading) return <LoadingOvalCenterIcon />;

  return (
    <ListSection id="product-file-list">
      <div>
        <div className="px-6 py-3 gap-4 grid grid-cols-7 text-sm font-medium">
          <div>{t(msg.general.name)}</div>
          <div>{t(msg.form.fileName)}</div>
          <div>{t(msg.media.size)}</div>
          <div>{t(msg.attribute.type)}</div>
          <div>{t(msg.product.product)}</div>
          <div>{t(msg.import.createdAt)}</div>
          <div></div>
        </div>
        {data?.items.map((item) => (
          <Row item={item} key={item.id} />
        ))}
      </div>
    </ListSection>
  );
}

function Row({ item }: { item: ProductFile }) {
  const dialogRef = useRef<HTMLDialogElement>(null);

  return (
    <div className="px-6 py-3 gap-4 grid grid-cols-7 text-sm font-medium">
      <div>{item.name || "--"}</div>
      <div>{item.originalFilename}</div>
      <div>{item.size}</div>
      <div>{item.type}</div>
      <div>
        <ul>
          {item.productIds.map((p) => (
            <li key={p.id}>{p.sku}</li>
          ))}
        </ul>
      </div>
      <div>{format(item.createdAt, "yyyy-MM-dd HH:mm")}</div>
      <div className="flex gap-2 items-start">
        <Link href={`/productFile/edit/${item.id}`}>
          <PencilIcon className="w-4 h-4" />
        </Link>
        <Link href={item.url} target="_blank">
          <ArrowDownTrayIcon className="w-4 h-4" />
        </Link>
        <button type="button" onClick={() => dialogRef.current?.showModal()}>
          <TrashIcon className="w-4 h-4 text-red-500" />
        </button>
      </div>
      <DeleteProductFileDialog dialogRef={dialogRef} productFileId={item.id} />
    </div>
  );
}
